import React, { FunctionComponent, useMemo } from 'react'
import classNames from 'classnames/bind'
import { type DocumentCategory } from '../../../../../../../model/DocumentCategory'
import { Icon, IconsType } from '../../../../../../shared'
import { ColorsV2, getV2Color } from '../../../../../../../design-system/colors_v2'
import { ChipProps } from '../../../../../../shared/chip/Chip/Chip.model'
import { breakMedium } from '../../../../../../../misc/responsive'
import { useWidthObserver } from '../../../../../../../hooks/utils'
import styles from './DocumentItem.module.scss'
import { getDefaultBgColor } from '../../../../../../../misc/colors.utilities'

import { DocumentItem as DocumentItemNonOpenable } from '@follow-health/fw_ui_library/dist/features/app/features/MedicalEvents/components/DocumentItem'

interface DocumentItemProps {
  documentId: number
  documentName: string
  documentInfos?: string
  documentCategory?: DocumentCategory
  navigateToNavTrans: () => void
  navigateToEditor: () => void
  isOpenable?: boolean
  colorPreset?: ChipProps['colorPreset']
}

const cx = classNames.bind(styles)

export const DocumentItem: FunctionComponent<DocumentItemProps> = ({
  documentName,
  documentInfos,
  documentCategory,
  navigateToNavTrans,
  navigateToEditor,
  colorPreset,
  isOpenable = true,
}) => {
  const isLowerThanBreakpoint = useWidthObserver(breakMedium)
  const icon = documentCategory?.icon as IconsType
  const docBorderColor = (documentCategory?.color as ColorsV2) || getDefaultBgColor()

  const isDanger = useMemo(() => colorPreset === 'danger', [colorPreset])
  const iconColor = useMemo(() => (isDanger ? ColorsV2.error : ColorsV2.contentDark1), [isDanger])
  const borderColor = useMemo(
    () => getV2Color(isDanger ? ColorsV2.error : docBorderColor),
    [docBorderColor, isDanger],
  )

  if (!isOpenable) {
    return (
      <DocumentItemNonOpenable
        isOpenable={isOpenable}
        documentName={documentName}
        isDanger={isDanger}
        tooltipDirection={'right'}
        borderColor={borderColor}
        navigateToNavTrans={navigateToNavTrans}
        navigateToEditor={navigateToEditor}
        icon={<Icon size="nano" icon={icon} color={iconColor} title={documentInfos} />}
        tooltipContent={`Une version plus récente de ce ${documentName?.toLowerCase()?.replace('.', '')} est disponible.`}
      />
    )
  }
  return (
    <div className={styles.container} {...(isLowerThanBreakpoint && { onClick: navigateToEditor })}>
      <div className={cx(styles.item, { danger: isDanger })} style={{ borderColor: borderColor }}>
        <div>
          <Icon size="nano" icon={icon} color={iconColor} title={documentInfos} />
        </div>
        <div className={styles.labelContent}>
          <span className={cx(styles.title, { danger: isDanger })}>{documentName}</span>
        </div>
        {isLowerThanBreakpoint && (
          <div className={styles.infoIcon}>
            {documentInfos && (
              <Icon size="micro" icon="infoCircle" color={ColorsV2.dsDark4} title={documentInfos} />
            )}
          </div>
        )}
      </div>

      {!isLowerThanBreakpoint && (
        <div className={styles.hoveredItem}>
          <div onClick={navigateToEditor} className={styles.iconContainer}>
            <Icon size="nano" icon="editAlt" color={ColorsV2.dsWhite} title="Éditer le document" />
          </div>
          <div onClick={navigateToNavTrans} className={styles.iconContainer}>
            <Icon
              size="nano"
              icon="eye"
              color={ColorsV2.dsWhite}
              title="Accéder à la navigation transverse"
            />
          </div>
          {documentInfos && (
            <div className={`${styles.iconContainer} ${styles.iconData}`}>
              <Icon size="nano" icon="infoCircle" color={ColorsV2.dsWhite} title={documentInfos} />
            </div>
          )}
        </div>
      )}
    </div>
  )
}
